import { CustomerCompanyService } from './customer-company.service';
import { ViewModelStore } from '../reducer';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AuthService } from '@rdlabo/shared/src/auth/auth.service';
import {
  IonButton,
  IonApp,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonMenu,
  IonMenuToggle,
  IonRouterLink,
  IonRouterOutlet,
  IonSplitPane,
  IonItemGroup,
} from '@ionic/angular';
import type { Observable } from 'rxjs';
import { filter, from, Subscription, switchMap, take, tap, timer } from 'rxjs';
import { SettingsService } from './settings/settings.service';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { environment } from '@rdlabo/shared/src/environments/environment';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { HttpClient } from '@angular/common/http';
import type { User } from '@rdlabo/ionic-angular-kit/auth-firebase';

@Component({
  selector: 'customer-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [
    RouterLinkActive,
    RouterLink,
    AsyncPipe,
    IonRouterLink,
    IonButton,
    IonApp,
    IonSplitPane,
    IonMenu,
    IonContent,
    IonList,
    IonItemGroup,
    IonListHeader,
    IonMenuToggle,
    IonItem,
    IonIcon,
    IonLabel,
    IonRouterOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  readonly vm = new ViewModel(this);
  readonly #auth = inject(AuthService);
  readonly #settings = inject(SettingsService);
  readonly #router = inject(Router);
  readonly #http = inject(HttpClient);

  readonly canManageUsers = signal(true);
  readonly authState$: Observable<User | null> = this.#auth.getState$();
  readonly initSubscription = new Subscription();
  readonly statusSubscription = new Subscription();

  constructor() {}

  ngOnInit() {
    this.initSubscription.add(
      this.#auth.getState$().subscribe((user) => {
        if (user) {
          this.vm.company.restore();
        } else {
          this.vm.company.clear();
        }
      }),
    );
    const verifiedUser$ = this.#auth.getState$().pipe(
      filter((user): user is User => Boolean(user?.emailVerified)),
      take(1),
    );
    this.statusSubscription.add(
      verifiedUser$
        .pipe(
          switchMap(() => timer(0, 8000)),
          switchMap(() => from(this.#settings.getStatus())),
        )
        .subscribe((status) => {
          this.canManageUsers.set(status.profile.permission === 'admin' || !!status.customerContext?.supportCompany);
          this.vm.dispatch(status);
        }),
    );
    this.initSubscription.add(
      verifiedUser$
        .pipe(
          switchMap(() => this.#router.events),
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          tap((event) => {
            FirebaseAnalytics.logEvent({
              name: event.url,
              params: { url: event.url },
            });
          }),
          switchMap((event) =>
            this.#http.post<void>(environment.api() + 'analytics', {
              route: event.url,
              type: 'customer',
            }),
          ),
        )
        .subscribe(),
    );
  }

  ngOnDestroy() {
    this.initSubscription.unsubscribe();
    this.statusSubscription.unsubscribe();
  }
}

class ViewModel extends ViewModelStore<AppComponent> {
  readonly company = inject(CustomerCompanyService);
}
